/* =========================================================================
   LEADERBOARD (§ v1.1.0) — one ranked list of the faithful, by LIFETIME XP.

   METRIC: total account XP (app.UserStats.Xp), already maintained by every
   completed session — so the board needs no new instrumentation. There is
   deliberately NO weekly/periodic reset in v1: that would need time-bucketed
   XP tracking that does not exist yet.

   SEEDED ENTRIES: five house players (BOGUS_PLAYERS) always occupy ranks 1–5,
   above every real player. They live here — client-side — so their names and
   scores are retunable by shipping a static bundle, with no API/DB deploy.
   Their scores FLOAT: each is the greater of its own base score and a gap above
   the entry below it, so a real player climbing past a base score never
   overtakes the house (see buildLeaderboard).

   GUESTS: may browse the whole board (the API read is anonymous); they simply
   never appear on it, and get an honest sign-in nudge instead of a fake rank —
   the same posture monastery.ts takes with the wallet.
   ========================================================================= */
import { esc } from './engine';
import { icon } from './ui/icons';
import { byId, show } from './dom';
import { track } from './telemetry';
import { showRegister } from './auth';
import { isAuthenticated, getCurrentUser, getLeaderboardTop, type LeaderboardApiEntry } from './api';

/* ------------------------------ seeded players ------------------------------ */

/** A house entry. `baseXp` is its nominal score; `gap` is the minimum XP it must
    hold over the entry directly BELOW it once real scores are floored in.
    Listed best-first (rank 1 → 5). Tune freely — nothing else depends on these. */
export interface BogusPlayer {
  displayName: string;
  baseXp: number;
  gap: number;
  rankName: string;
  rankIcon: string;
}

export const BOGUS_PLAYERS: readonly BogusPlayer[] = [
  // 2026-08-17 (Eric): reordered — BrotherAndrew takes #1 with a bigger score;
  // RosaryRunner drops to #2 keeping their old numbers.
  // 2026-09-06 (Eric): everyone bumped +5%, #1 bumped +8% (rounded to the
  // nearest 100 so the numbers keep reading hand-set, not computed).
  { displayName: 'BrotherAndrew',   baseXp: 169_500, gap: 4_200, rankName: 'Prior',    rankIcon: '⛪' },
  { displayName: 'RosaryRunner',    baseXp: 155_900, gap: 3_600, rankName: 'Abbot',    rankIcon: '👑' },
  { displayName: 'littleflower_23', baseXp: 138_000, gap: 3_100, rankName: 'Cantor',   rankIcon: '🌹' },
  { displayName: 'VigilKeeper',     baseXp: 131_000, gap: 2_700, rankName: 'Acolyte',  rankIcon: '🕯️' },
  { displayName: 'sr_bernadette',   baseXp: 124_100, gap: 2_200, rankName: 'Postulant', rankIcon: '📿' },
];

/* -------------------------------- entries -------------------------------- */

/** A row on the rendered board — seeded or real, already ranked. */
export interface LeaderboardEntry {
  /** 1-based position on the MERGED board (seeded entries included). */
  rank: number;
  /** null for seeded house entries (they have no account). */
  userId: string | null;
  displayName: string;
  xp: number;
  rankName: string | null;
  rankIcon: string | null;
  /** True for a seeded house entry. */
  isBogus: boolean;
  /** True for the signed-in player's own row. */
  isYou: boolean;
}

/** PURE: sort the real rows (defensively — never trust the wire order), floor the
    seeded entries above them, and emit one contiguously-ranked list.

    Real rows sort by XP descending with displayName as a stable tie-break, so two
    players on equal XP never swap places between two loads.

    The seeded floor is computed bottom-up from the top real score: the LAST house
    entry must clear the best real player by its own gap, and each entry above must
    clear the one below it by its gap. An entry whose base score already exceeds
    that floor keeps its (nicer-looking) base score. */
export function buildLeaderboard(
  real: readonly LeaderboardApiEntry[],
  currentUserId: string | null = null,
  bogus: readonly BogusPlayer[] = BOGUS_PLAYERS,
): LeaderboardEntry[] {
  const sortedReal = [...real].sort(
    (a, b) => b.xp - a.xp || a.displayName.localeCompare(b.displayName),
  );

  const topRealXp = sortedReal.length > 0 ? sortedReal[0].xp : 0;

  // Bottom-up: walk the seeded list in reverse, each one floored above the
  // running "highest score so far", then reverse back into best-first order.
  const seeded: LeaderboardEntry[] = [];
  let floor = topRealXp;
  for (let i = bogus.length - 1; i >= 0; i--) {
    const b = bogus[i];
    const xp = Math.max(b.baseXp, floor + b.gap);
    floor = xp;
    seeded.unshift({
      rank: 0, // assigned below
      userId: null,
      displayName: b.displayName,
      xp,
      rankName: b.rankName,
      rankIcon: b.rankIcon,
      isBogus: true,
      isYou: false,
    });
  }

  const realEntries: LeaderboardEntry[] = sortedReal.map((r) => ({
    rank: 0,
    userId: r.userId,
    displayName: r.displayName,
    xp: r.xp,
    rankName: r.rankName,
    rankIcon: r.rankIcon,
    isBogus: false,
    isYou: !!currentUserId && r.userId === currentUserId,
  }));

  return [...seeded, ...realEntries].map((e, i) => ({ ...e, rank: i + 1 }));
}

/** PURE: the signed-in player's own row, or null when they're not on the board
    (guest, or ranked below the fetched window). */
export function findYourEntry(entries: readonly LeaderboardEntry[]): LeaderboardEntry | null {
  return entries.find((e) => e.isYou) ?? null;
}

/** PURE: the medal class for a merged rank (drives the gold/silver/bronze styling). */
export function medalClass(rank: number): string {
  if (rank === 1) return 'gold';
  if (rank === 2) return 'silver';
  if (rank === 3) return 'bronze';
  return '';
}

/** PURE: the position text for a merged rank — '' on the podium (the tinted
    brand medal icon takes the slot), plain numerals below it. */
export function posLabel(rank: number): string {
  return medalClass(rank) ? '' : String(rank);
}

/** PURE: XP formatted for the board (thousands separated, never negative). */
export function fmtXp(xp: number): string {
  return Math.max(0, Math.round(xp)).toLocaleString('en-US');
}

/* -------------------------------- rendering -------------------------------- */

let entries: LeaderboardEntry[] = [];
let loaded = false;
let offline = false;

function rowHtml(e: LeaderboardEntry): string {
  const medal = medalClass(e.rank);
  const cls = ['lb-row', medal, e.isYou ? 'you' : ''].filter(Boolean).join(' ');
  // Rank-tier classification (Abbot/Prior/.../Novice) intentionally not shown
  // here (Eric, 2026-08-02): a public board is the wrong place for a label
  // that reads as "you're the lowest rank" next to someone's real name. The
  // rank-emoji column (.lb-ic) went with it (2026-09-13); rankIcon stays in
  // the data for the profile views.
  const sub = e.isYou ? 'You' : '';
  const pos = medal ? icon('medal', 18, medal) : esc(posLabel(e.rank));
  return (
    `<div class="${cls}">` +
    `<span class="lb-pos ${medal}">${pos}</span>` +
    `<span class="lb-main">` +
    `<span class="lb-name">${esc(e.displayName)}</span>` +
    (sub ? `<span class="lb-sub">${esc(sub)}</span>` : '') +
    `</span>` +
    `<span class="lb-xp">${esc(fmtXp(e.xp))}<span class="lb-xp-unit"> XP</span></span>` +
    `</div>`
  );
}

/** The "where do I stand" strip under the list: a sign-in nudge for guests, the
    player's own standing when they're on the board, an encouragement when not. */
function youStripHtml(): string {
  if (!isAuthenticated()) {
    return (
      `<div class="lb-you-strip guest">` +
      `<div class="lb-you-txt">Playing as guest. Guests aren't ranked. Create a free account and your XP starts counting toward the board.</div>` +
      `<button class="btn lb-signin" type="button" id="lbSignInBtn">Create free account</button>` +
      `</div>`
    );
  }
  const you = findYourEntry(entries);
  if (you) {
    return (
      `<div class="lb-you-strip">` +
      `<div class="lb-you-txt">You're <b>#${you.rank}</b> with <b>${esc(fmtXp(you.xp))} XP</b>. Keep climbing.</div>` +
      `</div>`
    );
  }
  return (
    `<div class="lb-you-strip">` +
    `<div class="lb-you-txt">You're not on the board yet. Finish a round to put your name up here.</div>` +
    `</div>`
  );
}

function render(): void {
  const body = document.getElementById('lbBody');
  if (!body) return;

  if (entries.length === 0) {
    body.innerHTML = `<div class="lb-empty">${loaded ? 'The board is quiet just now.' : 'Reading the board…'}</div>`;
    return;
  }

  const offlineNote = offline && loaded
    ? `<div class="lb-note-inline">Couldn't reach the board just now. Showing the house standings only.</div>`
    : '';

  body.innerHTML =
    offlineNote +
    `<div class="lb-list">${entries.map(rowHtml).join('')}</div>` +
    youStripHtml();

  const signIn = document.getElementById('lbSignInBtn');
  if (signIn) {
    signIn.onclick = () => {
      byId('bankbar').classList.add('hidden');
      showRegister();
    };
  }
}

/* -------------------------------- loading -------------------------------- */
async function load(): Promise<void> {
  const me = getCurrentUser();
  const r = await getLeaderboardTop(50);
  offline = !r.ok;
  loaded = true;
  const real = r.ok && r.data && Array.isArray(r.data.entries) ? r.data.entries : [];
  entries = buildLeaderboard(real, me?.userId ?? null);
}

/* -------------------------------- public -------------------------------- */
export async function openLeaderboard(): Promise<void> {
  track('AreaOpened', { area: 'leaderboard' });
  byId('bankbar').classList.add('hidden');
  show('leaderboard');
  // Instant paint: the house entries alone, so the screen is never blank.
  if (entries.length === 0) entries = buildLeaderboard([], getCurrentUser()?.userId ?? null);
  render();
  await load();
  render();
}

export function initLeaderboard(): void {
  byId('lbBackBtn').addEventListener('click', () => {
    byId('bankbar').classList.add('hidden');
    show('home');
  });
}
